import React from 'react';
import { ICONS_MAP, DocumentIcon2 } from '../components/shared/Icons';

/* ──────────────────────────────────────────────
   Brand — single source of truth for logo + colour
   ────────────────────────────────────────────── */
export const IGLU_ORANGE = '#F05223';            // sampled from iglu_logo.png
export const CLIENT_BLUE = '#3B82F6';            // every Group = 'Client' account
export const IGLU_LOGO   = '/static/images/iglu_logo_transparent.png';

export const IGLU_LOGO_DARK  = '/static/images/iglu_logo_transparent.png';   // login, forms, dark surfaces

export const BRANDS = {
  'IGLU South Yarra': {
    label:    'IGLU South Yarra',
    accent:   IGLU_ORANGE,
    logo:     IGLU_LOGO,
    logoDark: IGLU_LOGO_DARK,
  },
  // Add other clients here as they come on board:
  // 'Acme Towers': { label: 'Acme Towers', accent: '#0EA5E9', logo: '/static/images/acme.png', logoDark: '/static/images/acme_dark.png' },
};

// SNL's own look — the fallback for any client without a BRANDS entry
export const SNL_BRAND = {
  label:    'SNL',
  accent:   '#22d3ee',
  logo:     '/static/images/snl_dark_logo.png',
  logoDark: '/static/images/snl_dark_logo.png',
};

/* ──────────────────────────────────────────────
   Session Group values (Airtable `Group` field)
     staff   → TFN / ABN / TFN and ABN     (SNL employees & subcontractors)
     clients → Client / IGLU South Yarra   (Company = userJob = the client's business)
     former  → IGLU Flagstaff              (no longer a client)
   userJob is either an SNL job title (rolesAccess lists) or the client's business name.
   ────────────────────────────────────────────── */
export const STAFF_GROUPS  = ['TFN', 'ABN', 'TFN and ABN'];
export const CLIENT_GROUPS = ['Client', 'IGLU South Yarra'];
export const FORMER_GROUPS = ['IGLU Flagstaff'];

export const isStaff  = (user) => STAFF_GROUPS.includes(user?.group);
export const isClient = (user) => CLIENT_GROUPS.includes(user?.group);
export const isFormer = (user) => FORMER_GROUPS.includes(user?.group);

// Match on Group, then User_Job (a client's userJob is their business name)
export const getBrand = (user) =>
  BRANDS[user?.group] || BRANDS[user?.userJob] || null;

// Brand for any Group = 'Client' account: blue accent, their business name as
// the label (userJob IS the company), their headshot as the logo if one is on
// file, otherwise the SNL logo.
export const clientBrand = (user) => ({
  label:    user?.userJob || 'Client',
  accent:   CLIENT_BLUE,
  logo:     user?.headshot || SNL_BRAND.logo,
  logoDark: user?.headshot || SNL_BRAND.logoDark,
});

// Always returns something usable:
//   server-attached brand → named BRANDS entry (IGLU) → Group 'Client' → SNL staff default
export const getBrandOrDefault = (user) => {
  if (user?.brand) return user.brand;
  const named = getBrand(user);
  if (named) return named;
  if (user?.group === 'Client') return clientBrand(user);
  return SNL_BRAND;
};

/* ──────────────────────────────────────────────
   Icon helper (shared across consumers)
   Renders an <img> when `image` is passed, else an ICONS_MAP glyph
   ────────────────────────────────────────────── */
export const Icon = ({ name, color, image, alt = '', fallback = 'doc' }) => {
  if (image) {
    return (
      <img
        src={image}
        alt={alt}
        style={{ width: 22, height: 22, objectFit: 'contain', display: 'block' }}
      />
    );
  }
  const Comp = ICONS_MAP[name] || ICONS_MAP[fallback] || DocumentIcon2;
  return (
    <span style={{ color }}>
      <Comp />
    </span>
  );
};

/* ──────────────────────────────────────────────
   Role lists
   ────────────────────────────────────────────── */
const IT_ROLES = [
  'IT Operations',
];

const MANAGEMENT_ROLES = [
  'IT Operations',
  'Managing Director',
  'Operations Director',
  'Chief Executive Officer',
  'Contract Administrator',
  'Accounts',
];

const ADMIN_ROLES = [
  'IT Operations',
  'Digital Market Specialist',
];

const SUPERVISOR_ROLES = [
  'IT Operations',
  'Senior Operations Manager',
  'Operations Manager',
  'General Manager Operations',
  'Supervisor',
  'Manager Periodicals',
  'Operations Engineering Technologist',
  'Assistant Operations Manager',
  'Sales',
];

const EXECUTE_ROLES = [
  'IT Operations',
  'Subcontractor',
  'Employee',
];

const IGLU_ROLES = [
  'IT Operations',          // so you can see the IGLU section while testing
  'IGLU South Yarra',
];

// Clients' userJob is their business name (unlimited values), so the Client
// section is matched on Group instead — see groupAccess / hasSectionAccess.
const CLIENT_ROLES = [
  'IT Operations',          // so you can see the Client section while testing
];

/* ──────────────────────────────────────────────
   Message visibility — Messages tab (MessageCenter) and Job Messages (MessagesPanel)
   Enforced on the SERVER: profile.py OVERSIGHT_ROLES, loginportal.list_message_threads,
   profile.list_super_threads. Listed here so every role distinction lives in one place.
     · SNL staff in OVERSIGHT_ROLES   every thread — own ones as owner / recipient, all others
                                       read-only ("Management view": every lane, no reply box,
                                       never counted on the bell badge)
     · every other SNL staff member   only the threads they own or were sent
     · IGLU and all Clients           only the threads they were sent — never the overview,
                                       even if a client's business name matches a title below
   ────────────────────────────────────────────── */
export const OVERSIGHT_ROLES = [
  'IT Operations',
  'Managing Director',
  'Chief Executive Officer',
];
export const seesAllMessages = (user) => isStaff(user) && OVERSIGHT_ROLES.includes(user?.userJob);


export const rolesAccess = {
  it: IT_ROLES,
  itd: IT_ROLES,
  itm: IT_ROLES,
  management: MANAGEMENT_ROLES,
  managementd: MANAGEMENT_ROLES,
  managementm: MANAGEMENT_ROLES,
  admin: ADMIN_ROLES,
  admind: ADMIN_ROLES,
  adminm: ADMIN_ROLES,
  supervisor: SUPERVISOR_ROLES,
  supervisord: SUPERVISOR_ROLES,
  supervisorm: SUPERVISOR_ROLES,
  execute: EXECUTE_ROLES,
  executed: EXECUTE_ROLES,
  executem: EXECUTE_ROLES,
  iglu: IGLU_ROLES,
  iglud: IGLU_ROLES,
  iglum: IGLU_ROLES,
  client: CLIENT_ROLES,
  clientd: CLIENT_ROLES,
  clientm: CLIENT_ROLES,
};

// Sections that are ALSO granted by Group (Airtable `Group`), not just userJob.
export const groupAccess = {
  iglu:    ['IGLU South Yarra'],
  iglud:   ['IGLU South Yarra'],
  iglum:   ['IGLU South Yarra'],
  client:  ['Client'],
  clientd: ['Client'],
  clientm: ['Client'],
};

// Single check for ProfileHome / ProtectedRoutes: userJob in rolesAccess OR group in groupAccess
export const hasSectionAccess = (sectionId, user) =>
  (rolesAccess[sectionId] || []).includes(user?.userJob) ||
  (groupAccess[sectionId] || []).includes(user?.group);

/* ──────────────────────────────────────────────
   Nav sections
   ────────────────────────────────────────────── */
export const navSections = [
  {
    id: 'itd',
    label: 'IT Operations',
    icon: 'layers',
    accentColor: '#8B5CF6',
    responsive: 'desktop-only',
    items: [
      { id: 'itworkflowitd', label: 'Workflow', route: '/adminfiltersd', icon: 'plus' },
      { id: 'adminworkflowdit2', label: 'Admin', route: '/adminfiltersd3', icon: 'users' },
      { id: 'itavailabilityd', label: 'Casuals Work Responses', route: '/superworkavailabilityd', icon: 'calendar' },
      { id: 'itdataitd', label: 'IT Data', route: '/itworkloadd', icon: 'users' },
      { id: 'superonboardingitd', label: 'Account', route: '/superonboardd', icon: 'users' },

    ],
  },
  {
    id: 'managementd',
    label: 'Management',
    icon: 'users',
    accentColor: '#14B8A6',
    responsive: 'desktop-only',
    items: [
      { id: 'managementworkflowmanaged', label: 'Workflow', route: '/adminfiltersd', icon: 'plus' },
      { id: 'adminworkflowdmanage2', label: 'Admin', route: '/adminfiltersd3', icon: 'users' },
      { id: 'mangavailabilityd', label: 'Casuals Work Responses', route: '/superworkavailabilityd', icon: 'calendar' },
      { id: 'superonboardingmanaged', label: 'Account', route: '/superonboardd', icon: 'users' },
    ],
  },
  {
    id: 'admind',
    label: 'Administration',
    icon: 'briefcase',
    accentColor: '#F59E0B',
    responsive: 'desktop-only',
    items: [
      { id: 'adminworkflowadmind', label: 'Workflow', route: '/adminfiltersd', icon: 'plus' },
      { id: 'adminworkflowdadmin2', label: 'Admin', route: '/adminfiltersd2', icon: 'users' },
      { id: 'adminavailabilityd', label: 'Casuals Work Responses', route: '/superworkavailabilityd', icon: 'calendar' },
      { id: 'superonboardingadmind', label: 'Account', route: '/superonboardd', icon: 'users' },
    ],
  },
  {
    id: 'adminm',
    label: 'Administration',
    icon: 'briefcase',
    accentColor: '#F59E0B',
    responsive: 'mobile-only',
    items: [
      { id: 'leavesuperadminm', label: 'Workflow', route: '/superleaveaddsm', icon: 'plus' },
      { id: 'clientadminm', label: 'Update', route: '/superclientaddsm', icon: 'plus' },
      { id: 'adminavailabilitym', label: 'Casuals Work Responses', route: '/superworkavailabilitym', icon: 'calendar' },
      { id: 'calendarsuperadminm', label: 'Calendar', route: '/calendaradminmobile', icon: 'calendar' },
      { id: 'tesntagsuperadminm', label: 'Compliance', route: '/testntagadminmobile', icon: 'edit' },
      { id: 'recruitsuperadminm', label: 'Recruitment', route: '/superrecruitsm', icon: 'users' },
      { id: 'personalmobileadminm', label: 'Account', route: '/personalonboardm', icon: 'lock' },
    ],
  },
  {
    id: 'managementm',
    label: 'Management',
    icon: 'users',
    accentColor: '#14B8A6',
    responsive: 'mobile-only',
    items: [
      { id: 'leavesupermanagem', label: 'Workflow', route: '/superleaveaddsm', icon: 'plus' },
      { id: 'clientmanagem', label: 'Update', route: '/superclientaddsm', icon: 'plus' },
      { id: 'mangavailabilitym', label: 'Casuals Work Responses', route: '/superworkavailabilitym', icon: 'calendar' },
      { id: 'calendarsupermanagem', label: 'Calendar', route: '/calendaradminmobile', icon: 'calendar' },
      { id: 'tesntagsupermanagem', label: 'Compliance', route: '/testntagadminmobile', icon: 'edit' },
      { id: 'recruitsupermanagem', label: 'Recruitment', route: '/superrecruitsm', icon: 'users' },
      { id: 'personalmobilemanagem', label: 'Account', route: '/personalonboardm', icon: 'lock' },
    ],
  },
  {
    id: 'supervisord',
    label: 'Operations',
    icon: 'shield',
    accentColor: '#22D3EE',
    responsive: 'desktop-only',
    items: [
      { id: 'superworkflowsuperd', label: 'Workflow', route: '/supervisorfiltersd', icon: 'plus' },
      { id: 'adminworkflowdadmin4', label: 'Admin', route: '/adminfiltersd4', icon: 'users' },
      { id: 'superavailabilityd', label: 'Casuals Work Responses', route: '/superworkavailabilityd', icon: 'calendar' },
      { id: 'superonboardingsuperd', label: 'Account', route: '/superonboardd', icon: 'users' },
    ],
  },
  {
    id: 'supervisorm',
    label: 'Operations',
    icon: 'shield',
    accentColor: '#22D3EE',
    responsive: 'mobile-only',
    items: [
      { id: 'leavesuperm', label: 'Workflow', route: '/superleaveaddsm', icon: 'plus' },
      { id: 'clientsuperm', label: 'Update', route: '/superclientaddsm', icon: 'plus' },
      { id: 'superavailabilitym', label: 'Casuals Work Responses', route: '/superworkavailabilitym', icon: 'calendar' },
      { id: 'calendarsuperm', label: 'Calendar', route: '/calendaradminmobile', icon: 'calendar' },
      { id: 'tesntagsuperm', label: 'Compliance', route: '/testntagadminmobile', icon: 'edit' },
      { id: 'recruitsuperm', label: 'Recruitment', route: '/superrecruitsm', icon: 'users' },
      { id: 'personalmobilesuperm', label: 'Account', route: '/personalonboardm', icon: 'lock' },
    ],
  },
  {
    id: 'executed',
    label: 'Staff',
    icon: 'zap',
    accentColor: '#F97316',
    responsive: 'desktop-only',
    items: [
      { id: 'staffworkflowd', label: 'Workflow', route: '/stafffiltersd', icon: 'plus' },
      { id: 'superonboardingstaffd', label: 'Account', route: '/superonboardd', icon: 'users' },
      { id: 'workavailabilityd', label: 'Work Availability', route: '/workavailabilityd', icon: 'calendar', requires: 'casual' },
    ],
  },
  {
  id: 'executem',
  label: 'Staff',
  icon: 'zap',
  accentColor: '#F97316',
  responsive: 'mobile-only',
  items: [
    { id: 'workflowm',        label: 'Workflow',          route: '/staffaddsm',       icon: 'plus' },
    { id: 'personalmobilem',  label: 'Account',           route: '/personalonboardm', icon: 'lock' },
    { id: 'workavailabilitym', label: 'Work Availability', route: '/workavailabilitym', icon: 'calendar', requires: 'casual' },
  ],
}
,

  /* ── IGLU South Yarra — same desktop/mobile split as Staff ── */
  {
    id: 'iglud',
    label: 'IGLU South Yarra',
    icon: 'pin',
    image: IGLU_LOGO,
    accentColor: IGLU_ORANGE,
    responsive: 'desktop-only',
    items: [
      { id: 'igluworkflowd', label: 'Workflow', route: '/iglufiltersd', icon: 'plus' },
      
    ],
  },
  {
    id: 'iglum',
    label: 'IGLU South Yarra',
    icon: 'pin',
    image: IGLU_LOGO,
    accentColor: IGLU_ORANGE,
    responsive: 'mobile-only',
    items: [
      { id: 'igluworkflowm', label: 'Workflow', route: '/igluaddsm', icon: 'plus' },
     
    ],
  },

  /* ── Client (all other SNL clients, Group = 'Client') ── */
  {
    id: 'clientd',
    label: 'Client Portal',
    icon: 'users',
    accentColor: CLIENT_BLUE,
    responsive: 'desktop-only',
    items: [
      { id: 'clientworkflowd', label: 'Workflow', route: '/clientfiltersd', icon: 'plus' },
      
    ],
  },
  {
    id: 'clientm',
    label: 'Client Portal',
    icon: 'users',
    accentColor: CLIENT_BLUE,
    responsive: 'mobile-only',
    items: [
      { id: 'clientworkflowm', label: 'Workflow', route: '/clientaddsm', icon: 'plus' },
      
    ],
  },
];

/* ──────────────────────────────────────────────
   Accent colours — keyed by section ID
   ────────────────────────────────────────────── */
export const PAGE_ACCENT = {
  it:          '#8B5CF6',
  itd:         '#8B5CF6',
  itm:         '#8B5CF6',
  management:  '#14B8A6',
  managementd: '#14B8A6',
  managementm: '#14B8A6',
  admin:       '#F59E0B',
  admind:      '#F59E0B',
  adminm:      '#F59E0B',
  supervisor:  '#22D3EE',
  supervisord: '#22D3EE',
  supervisorm: '#22D3EE',
  execute:     '#F97316',
  executed:    '#F97316',
  executem:    '#F97316',
  iglu:        IGLU_ORANGE,
  iglud:       IGLU_ORANGE,
  iglum:       IGLU_ORANGE,
  client:      CLIENT_BLUE,
  clientd:     CLIENT_BLUE,
  clientm:     CLIENT_BLUE,
};


export const ITEM_REQUIREMENTS = {
  casual: (user) => !!user?.isCasual,
};

// one item: does this user meet its `requires`?
export const itemVisible = (item, user) => {
  if (!item?.requires) return true;
  const check = ITEM_REQUIREMENTS[item.requires];
  return check ? check(user) : false;
};

// a whole section: its items with the hidden ones removed
export const visibleItems = (section, user) =>
  (section?.items || []).filter((item) => itemVisible(item, user));

/* ──────────────────────────────────────────────
   Role → accent (IGLU first so the brand colour wins)
   ────────────────────────────────────────────── */
// Accepts either a userJob string (legacy callers) or the whole user object
export const getRoleAccent = (userOrJob) => {
  const user   = typeof userOrJob === 'object' && userOrJob ? userOrJob : null;
  const userJob = user ? user.userJob : userOrJob;
  if (user?.group === 'IGLU South Yarra')        return PAGE_ACCENT.iglu;
  if (user?.group === 'Client')                  return PAGE_ACCENT.client;
  if (!userJob) return PAGE_ACCENT.execute;
  if (userJob === 'IGLU South Yarra')            return PAGE_ACCENT.iglu;
  if (rolesAccess.it.includes(userJob))          return PAGE_ACCENT.it;
  if (rolesAccess.management.includes(userJob))  return PAGE_ACCENT.management;
  if (rolesAccess.admin.includes(userJob))       return PAGE_ACCENT.admin;
  if (rolesAccess.supervisor.includes(userJob))  return PAGE_ACCENT.supervisor;
  if (rolesAccess.execute.includes(userJob))     return PAGE_ACCENT.execute;
  return PAGE_ACCENT.execute;
};

/* ──────────────────────────────────────────────
   Role label — Group first (IGLU / Client), then the SNL job lists.
   Accepts a userJob string (legacy) or the whole user object.
   ────────────────────────────────────────────── */
export const getRoleLabel = (userOrJob) => {
  const user    = typeof userOrJob === 'object' && userOrJob ? userOrJob : null;
  const userJob = user ? user.userJob : userOrJob;
  if (user?.group === 'IGLU South Yarra')        return 'IGLU South Yarra';
  if (user?.group === 'Client')                  return userJob || 'Client';  // a client's userJob is their business
  if (!userJob) return 'Staff';
  if (rolesAccess.it.includes(userJob))          return 'IT Operations';
  if (rolesAccess.management.includes(userJob))  return 'Management';
  if (rolesAccess.admin.includes(userJob))       return 'Administration';
  if (rolesAccess.supervisor.includes(userJob))  return 'Operations';
  if (rolesAccess.execute.includes(userJob))     return 'Staff';
  return 'Staff';
};